/**
 * DSCResultsTabPanel.jsx
 *
 * One DSC results tab panel = filter sidebar (FacetSection) + PLP grid
 * (PLPProductCard with RegionAvailabilityBadge slot) + useFilters hook.
 *
 * Citations (port-first):
 *  - Group D `FacetSection`         (`src/components/product/FacetSection.jsx`)
 *    — reused VERBATIM as the left-rail facet rendering.
 *  - Group D `PLPProductCard`       (`src/components/product/PLPProductCard.jsx`)
 *    — reused VERBATIM as the grid card. Region availability passed in via
 *    the `availabilityBadge` slot prop introduced in SP3.B.1.
 *  - Group D `RegionAvailabilityBadge`
 *    (`src/components/wizard/RegionAvailabilityBadge.jsx`) — reused VERBATIM.
 *  - Group D `useFilters`           (`src/hooks/useFilters.js`) — reused
 *    VERBATIM. Returns `filteredProducts`, `facetsWithCounts`,
 *    `activeFilters`, `toggleFilterValue`, `clearAllFilters`,
 *    `activeFilterCount`.
 *  - Legacy LWC `searchResults.html` lines 244-308 — the results section
 *    (aside-section facet rail + main-section product grid + reset-all
 *    link). PWA equivalent uses the Group D components instead of the LWC
 *    `c-search-filter` and `c-search-layout` modules — port-first
 *    primitives.
 *  - Plan §0 row `DSCResultsTabPanel.jsx`: "reuses Group D
 *    `src/components/product/FacetSection.jsx` +
 *    `src/components/product/PLPProductCard.jsx` +
 *    `src/hooks/useFilters.js` +
 *    `src/components/wizard/RegionAvailabilityBadge.jsx`. One tab panel =
 *    filter sidebar + PLP grid."
 *
 * Facet config:
 *  - `facetIds` is the array of fields driving facet rendering for this tab,
 *    sourced from `DSC_TAB_FACETS[tabSlug]` in `data/dscSlotConfig.js`.
 *  - Each id is mapped into a `{ id, label, field }` shape acceptable by
 *    `useFilters`. Label uses a static lookup with a sensible fallback.
 *
 * @module components/dsc/DSCResultsTabPanel
 * @since 2026-05-20 (Group E SP2 — shared DSC primitives)
 */

import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FacetSection } from '@components/product/FacetSection';
import { PLPProductCard } from '@components/product/PLPProductCard';
import RegionAvailabilityBadge from '@components/wizard/RegionAvailabilityBadge';
import { useFilters } from '@hooks/useFilters';
import { DSC_TAB_FACETS, resolveFacetCascadeDispatch } from '@data/dscSlotConfig';
import { useDSC } from '@contexts/DSCContext';

// Facet-label lookup — keys MUST match the `facetIds` (field names) in
// `data/dscSlotConfig.js#DSC_TAB_FACETS`. Verbatim from plan §1.4 wording.
const FACET_LABELS = {
  // TH facets
  diameterMm: 'Diameter (mm)',
  lengthMm: 'Length (mm)',
  compatibleRockDrill: 'Rock drill model',
  thread: 'Thread',
  couplingType: 'Coupling type',
  threadF2: 'Rod-end thread',
  threadF1: 'Shank-end thread',
  rodType: 'Rod type',
  thread2: 'Rod-end thread',
  pilotBitThread: 'Pilot bit thread',
  bitDiameterMm: 'Bit diameter',
  buttonShape: 'Button shape',
  carbideGrade: 'Carbide grade',
  // DTH facets
  hammerFamily: 'Hammer family',
  hammerSize_in: 'Hammer size (in)',
  shankType: 'Shank type',
  hammerOutsideDiameter_mm: 'Outside diameter (mm)',
  topsubPinOption: 'Pin option',
  diameter_mm: 'Diameter (mm)',
  length_mm: 'Length (mm)',
  bitHeadDiameter_mm: 'Bit head diameter (mm/in)',
  bitFaceDesign: 'Face design',
  carbideShape: 'Carbide shape',
};

// Attributes displayed on each card (first 4 are rendered by PLPProductCard).
// Keys are the facet field names; default label/suffix surfaced from
// FACET_LABELS lookup.
function buildCardAttributes(facetIds) {
  return facetIds.slice(0, 4).map((field) => ({
    field,
    label: FACET_LABELS[field] || field,
    suffix: '',
  }));
}

/**
 * @param {Object} props
 * @param {string} props.tabSlug — current tab id (e.g. 'shank-adapters').
 * @param {string} [props.tabId] — alias for tabSlug (consumed by SP3
 *        DTHDSCLayout which passes the tab id under this prop name).
 * @param {Object[]} props.products — products visible in this tab (already
 *        narrowed by the DSC cascade — caller is responsible).
 * @param {string[]} props.facetIds — ordered facet field names for this tab.
 *        When omitted, falls back to `DSC_TAB_FACETS[resolvedTabSlug]` from
 *        `data/dscSlotConfig.js` (canonical per plan §1.4).
 * @param {string} [props.dataTestid]
 */
export default function DSCResultsTabPanel({
  tabSlug,
  tabId,
  products = [],
  facetIds,
  onAddToCart,
  dataTestid = 'dsc-results-tab-panel',
}) {
  const resolvedTabSlug = tabSlug || tabId;
  const resolvedFacetIds = Array.isArray(facetIds)
    ? facetIds
    : DSC_TAB_FACETS[resolvedTabSlug] || [];
  const navigate = useNavigate();

  const facetConfig = useMemo(
    () =>
      resolvedFacetIds.map((field) => ({
        id: field,
        label: FACET_LABELS[field] || field,
        field,
      })),
    [resolvedFacetIds]
  );

  const {
    filteredProducts,
    facetsWithCounts,
    activeFilters,
    toggleFilterValue,
    clearAllFilters,
    activeFilterCount,
  } = useFilters(products, facetConfig);

  // Task #15 — bidirectional sync. When a facet chip is toggled and the
  // facet maps 1:1 to a cascade slot, dispatch SET_SELECTION so the
  // cascade dropdowns + downstream slots re-compute. Multi-select (2+
  // values) skips the dispatch — cascade slots are single-select and
  // multi-select doesn't map cleanly. Empty (last chip cleared) sends
  // 'None' to trigger the reducer's clear branch.
  const { setSelection } = useDSC();
  const handleFacetToggle = useCallback(
    (facetId, value) => {
      toggleFilterValue(facetId, value);
      const dispatchArgs = resolveFacetCascadeDispatch(
        activeFilters,
        resolvedTabSlug,
        facetId,
        value
      );
      if (dispatchArgs) {
        setSelection(dispatchArgs.slotId, dispatchArgs.dropdownKey, dispatchArgs.value);
      }
    },
    [resolvedTabSlug, activeFilters, toggleFilterValue, setSelection]
  );

  const cardAttributes = useMemo(
    () => buildCardAttributes(resolvedFacetIds),
    [resolvedFacetIds]
  );

  return (
    <div
      data-testid={dataTestid}
      data-tab={resolvedTabSlug}
      className="flex flex-col md:flex-row gap-6 mt-4"
    >
      {/* Facet sidebar */}
      <aside className="w-full md:w-64 flex-shrink-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-n3">
          <h2 className="font-sandvik-headline font-normal text-sm text-carbon-black">
            Filters
          </h2>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearAllFilters}
              data-testid="dsc-reset-all"
              className="text-xs text-electric-blue underline hover:text-carbon-black focus:outline-none focus:ring-2 focus:ring-electric-blue"
            >
              Reset all
            </button>
          )}
        </div>
        <div className="flex flex-col">
          {facetsWithCounts.map((facet) => (
            <FacetSection
              key={facet.id}
              facet={facet}
              activeFilters={activeFilters}
              onToggle={handleFacetToggle}
            />
          ))}
        </div>
      </aside>

      {/* PLP grid */}
      <main className="flex-1 min-w-0">
        {filteredProducts.length === 0 ? (
          <div
            data-testid="dsc-empty-results"
            className="p-8 text-center text-n5 font-sandvik-text"
          >
            <p className="mb-2">No products match your selection.</p>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-electric-blue underline focus:outline-none focus:ring-2 focus:ring-electric-blue"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div
            data-testid="dsc-results-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {filteredProducts.map((product) => (
              <PLPProductCard
                key={product.itemCode}
                product={product}
                attributes={cardAttributes}
                viewMode="grid"
                onProductClick={() =>
                  navigate(`/products/detail/${encodeURIComponent(product.itemCode)}`)
                }
                onAddToCart={onAddToCart ? () => onAddToCart(product) : undefined}
                availabilityBadge={
                  <RegionAvailabilityBadge itemCode={product.itemCode} />
                }
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
